// 기술 마스터 — **파트너 기술 분류 코드(partnerTechCodes.ts)가 유일한 기준**(사용자 확정 2026-11-02 「코드로 통일」).
//  코드·이름은 partners/partnerTechCodes.ts 하나에서 온다. 여기는 그 코드에 붙는 엔진 정보만 둔다:
//   ① 자유텍스트(옛 「주요 기술」·패키지 컴포넌트·회의록 표기) → 25코드 정규화 (normalizeTechName)
//      — 옛 세부 표기(DPS·DAS·DPC·PDA·RFID·Vision Picking …)는 전부 **별칭**이다. 따로 기준을 두지 않는다.
//   ② 그룹(equipment / software / device / service)
//   ③ 설비류만 추정 처리량 스펙 — 코드 하나에 처리량 하나(여러 세부 기술은 범위를 합침).
//      ★estimated: 업계 공개 추정치(Witron·Interlake Mecalux·GoASRS·Honeywell·Kardex 등)이며 파트너 실값이 아님.
//  미등록 표기는 버리지 않고 "미분류"(UNCLASSIFIED)로 보존한다.
import { PARTNER_TECH_CODES, PARTNER_TECH_GROUPS } from "./partners/partnerTechCodes";

export type TechGroup = "equipment" | "software" | "device" | "service";

export type TechSpec = {
  /** 처리량 표기 (예: "190~350 picks/h") — 사람이 읽는 문자열, 사이징 계산은 min/max 사용 */
  label: string;
  /** label 의 영문 — 화면 언어가 en 일 때만(비면 label). 사이징·문서 출력은 label 그대로. */
  labelEn?: string;
  min?: number;
  max?: number;
  unit?: string;
  /** ★항상 true — 업계 공개 추정치. 파트너 실값 아님(제안서에 "추정치·정밀 사이징 아님" 표시) */
  estimated: true;
  note?: string;
};

export type TechMasterEntry = {
  /** 25코드 (예: PIC-DPSDAS) */
  code: string;
  /** 분류 이름 (partnerTechCodes name) */
  label: string;
  /** 화면 표기 */
  displayKo: string;
  /** 상위 그룹 이름(자동 보관 · 피킹·핸들링 …) */
  category: string;
  group: TechGroup;
  /** 흡수할 표기 — 옛 세부 기술 이름·코드 포함 (소문자·영숫자만 비교) */
  aliases?: string[];
  /** 설비류만 — SW/디바이스/서비스는 스펙 없음(카테고리·역할로만 추천) */
  spec?: TechSpec;
  note?: string;
};

/** 미등록 tech 보존용 코드 */
export const UNCLASSIFIED_CODE = "unclassified";
export const UNCLASSIFIED_LABEL = "미분류";

type EngineInfo = { group: TechGroup; aliases: string[]; spec?: TechSpec; en: string };

// 코드별 엔진 정보. 별칭에는 옛 techMaster 세부 코드(dps · mini_sorter …)와 표기를 모두 넣는다 —
//  저장돼 있던 값·패키지 컴포넌트·회의록이 같은 코드로 모인다.
const ENGINE: Record<string, EngineInfo> = {
  "STO-ASRS": { group: "equipment", en: "AS/RS (Automated storage)",
    aliases: ["asrs", "as rs", "as-rs", "automated storage", "AS/RS (자동창고)", "자동창고", "stacker crane", "스태커크레인"],
    spec: { label: "미니로드 60~120 / 팔레트 20~40 /h·aisle", labelEn: "Mini-load 60–120 / pallet 20–40 per h·aisle", min: 20, max: 120, unit: "cycles/h·aisle", estimated: true, note: "미니로드 60~120, 팔레트 20~40" } },
  "STO-SHUT": { group: "equipment", en: "Shuttle", aliases: ["shuttle", "셔틀"],
    spec: { label: "토트 200~1,000+ / 팔레트 50~120+ /h·aisle", labelEn: "Tote 200–1,000+ / pallet 50–120+ per h·aisle", min: 50, max: 1000, unit: "cycles/h·aisle", estimated: true, note: "토트 200~1,000+, 팔레트 50~120+" } },
  "STO-VLM": { group: "equipment", en: "VLM (Vertical lift module)", aliases: ["vlm", "vertical lift module", "수직 리프트"] },
  "STO-AUTO": { group: "equipment", en: "AutoStore", aliases: ["autostore", "auto store", "오토스토어"],
    spec: { label: "GTP 300~600 lines/h/station", labelEn: "GTP 300–600 lines/h/station", min: 300, max: 600, unit: "lines/h/station", estimated: true, note: "bin presentation" } },
  "MOV-AMR": { group: "equipment", en: "AMR (Autonomous mobile robot)", aliases: ["amr", "AMR (자율주행로봇)", "자율주행로봇"],
    spec: { label: "분류 2,000~3,000+ sorts/h (구성 의존)", labelEn: "Sorting 2,000–3,000+ sorts/h (configuration-dependent)", min: 2000, max: 3000, unit: "sorts/h", estimated: true, note: "구성(대수·레이아웃) 의존" } },
  "MOV-AGV": { group: "equipment", en: "AGV (Automated guided vehicle)", aliases: ["agv", "AGV (무인운반차)", "무인운반차"],
    spec: { label: "AMR류 (경로 고정)", labelEn: "AMR-class (fixed path)", estimated: true, note: "경로 고정형 — AMR 준용" } },
  "MOV-CONV": { group: "equipment", en: "Conveyor", aliases: ["conveyor", "conveyer", "conveyors", "컨베이어"],
    spec: { label: "설계 의존", labelEn: "Design-dependent", estimated: true, note: "라인 설계 의존 — 조건부" } },
  "MOV-DEL": { group: "equipment", en: "Delivery · special-purpose mobile robot",
    aliases: ["robot", "로봇 (범용)", "자율배송", "배송로봇", "서비스로봇"],
    spec: { label: "용도별 상이", labelEn: "Varies by application", estimated: true, note: "특수 목적 이동 로봇 — 용도 확정 후 사이징" } },
  "SOR-SORT": { group: "equipment", en: "Sorter",
    aliases: ["sorter", "mini_sorter", "mini sorter", "minisorter", "mini-sorter", "미니 소터", "sorter_3d", "3d sorter", "3d소터", "3D 소터",
      "e_sort", "esort", "e sort", "E-Sort", "이소터", "분류기", "소터", "sorting system", "sortation", "high speed sorter",
      "wheel sorter", "cross belt sorter", "slide shoe sorter", "tilt tray sorter", "휠소터", "크로스벨트소터", "슬라이드슈소터", "틸트트레이소터"],
    spec: { label: "소형 5,000~15,000 units/h (대형은 설계 의존)", labelEn: "Small: 5,000–15,000 units/h (large: design-dependent)", min: 5000, max: 15000, unit: "units/h", estimated: true } },
  "PIC-DPSDAS": { group: "equipment", en: "DPS / DAS / DPC",
    aliases: ["dps", "das", "dpc", "lcd", "DPS (Pick to Light)", "DAS (Put to Light)", "pick to light", "put to light", "pick-to-light", "put-to-light",
      "digital picking cart", "picking cart", "DPC (디지털 피킹 카트)", "LCD 표시기", "indicator"],
    spec: { label: "DPS·DAS 190~350 / DPC 100~250 lines/h/인", labelEn: "DPS·DAS 190–350 / DPC 100–250 lines/h per person", min: 100, max: 350, unit: "lines/h/인", estimated: true, note: "DPS·DAS·DPC 범위를 합침" } },
  "PIC-ROBOT": { group: "equipment", en: "Robot picking", aliases: ["picking_robot", "picking robot", "picking-robot", "pick robot", "피킹 로봇"],
    spec: { label: "로봇 피킹 (픽/h 구성 의존)", labelEn: "Robotic picking (picks/h depend on configuration)", estimated: true, note: "그리퍼·SKU 형상 의존" } },
  "PIC-VOICE": { group: "equipment", en: "Voice picking", aliases: ["voice", "voice picking", "보이스 피킹"],
    spec: { label: "100~250 lines/h/인", labelEn: "100–250 lines/h per person", min: 100, max: 250, unit: "lines/h/인", estimated: true } },
  "PIC-PAL": { group: "equipment", en: "Palletizer / Depalletizer", aliases: ["palletizer", "depalletizer", "de-palletizer", "팔레타이저", "디팔레타이저"],
    spec: { label: "팔레타이징·디팔레타이징 (cycle 기반)", labelEn: "Palletizing · depalletizing (cycle-based)", estimated: true, note: "케이스/cycle 기반 사이징" } },
  "PAC-PACK": { group: "equipment", en: "Packaging",
    aliases: ["packaging", "pakaging", "packing", "패키징(포장 자동화)", "wrapper", "랩핑기", "랩핑", "wrapping", "stretch wrapper", "랩핑기 (스트레치 래핑)",
      "case_sealer", "제함&봉함기", "제함·봉함기", "제함", "봉함", "case erector", "carton sealer"],
    spec: { label: "포장 자동화 (구성 의존)", labelEn: "Packaging automation (configuration-dependent)", estimated: true, note: "조건부" } },
  "PAC-LABEL": { group: "equipment", en: "Autolabeler", aliases: ["autolabeler", "auto labeler", "auto-labeler", "오토라벨러"],
    spec: { label: "라벨링 (라인속도 의존)", labelEn: "Labeling (line-speed dependent)", estimated: true, note: "조건부" } },
  "SEN-BAR": { group: "device", en: "Barcode / RFID / PDA",
    aliases: ["pda", "rfid", "scanner", "barcode", "barcode scanner", "바코드스캐너", "스캐너", "wearable", "wearable device", "wearables", "웨어러블"] },
  "SEN-SAFE": { group: "device", en: "Security · Safety",
    aliases: ["safety", "safety system", "안전", "안전 설비", "보안", "security", "cctv", "출입통제", "cctv / 출입통제"] },
  "SEN-VIS": { group: "device", en: "Machine vision / inspection",
    aliases: ["vision", "vision system", "vision_picking", "vision picking", "visionpicking", "비전 피킹 (AR)", "smart_glass", "smart glass", "smartglass",
      "smart glasses", "스마트 글라스 (AR)", "비전 (검사·인식)", "machine vision"] },
  "SW-WMS": { group: "software", en: "WMS (Warehouse management)", aliases: ["wms", "WMS (창고관리)", "창고관리"] },
  "SW-WES": { group: "software", en: "WES (Execution)", aliases: ["wes", "WES (실행)"] },
  "SW-WCS": { group: "software", en: "WCS / ECS / control", aliases: ["wcs", "ecs", "WCS (설비제어)", "scada", "스카다", "monitoring system", "SCADA (모니터링)"] },
  "SW-ROBOT": { group: "software", en: "Robot fleet management SW", aliases: ["robot fleet", "fleet management", "로봇 관제"] },
  "SW-AI": { group: "software", en: "AI · Orchestration",
    aliases: ["ai", "ai agent", "ai agent / api", "orchestration", "오케스트레이션", "ai 배차", "ai 배차·최적화", "decision_infra", "decision infrastructure",
      "decision infra", "의사결정 인프라 (AI)", "semantic_ontology", "semantic ontology", "시맨틱 온톨로지 (AI)"] },
  "SERV-SIM": { group: "software", en: "Simulation / Digital twin", aliases: ["digital_twin", "digital twin", "digitaltwin", "디지털 트윈", "simulation"] },
  "SERV-SI": { group: "service", en: "SI / design · build",
    aliases: ["total_solutions", "total solutions", "total solution", "통합 솔루션 (SI)", "automation_design", "automation design", "자동화 설계", "consulting", "컨설팅", "si"] },
  "SERV-MNT": { group: "service", en: "Maintenance", aliases: ["maintenance", "as_service", "a/s", "as", "a s", "유지보수"] },
  "SERV-OPS": { group: "service", en: "Logistics operation / 3PL", aliases: ["3pl", "물류 운영", "fulfillment"] },
};

const GROUP_LABEL = new Map(PARTNER_TECH_GROUPS.map((g) => [g.key, g.label]));

export const TECH_MASTER: TechMasterEntry[] = PARTNER_TECH_CODES.map((c) => {
  const e = ENGINE[c.code];
  return {
    code: c.code,
    label: c.name,
    displayKo: c.name,
    category: GROUP_LABEL.get(c.group) || c.group,
    group: e?.group || "equipment",
    aliases: e?.aliases || [],
    spec: e?.spec,
  };
});

const masterByCode = new Map<string, TechMasterEntry>();
TECH_MASTER.forEach((t) => masterByCode.set(t.code, t));

export function getTechEntry(code: string): TechMasterEntry | undefined {
  return masterByCode.get(code);
}

/** 비교키: 소문자 + 영숫자 이외 제거 — "AS/RS"→"asrs", "Pick-to-Light"→"picktolight" */
function compareKey(raw: string): string {
  return String(raw || "").toLowerCase().replace(/[^a-z0-9가-힣]+/g, "");
}

// alias/원문/코드 → 25코드 사전 (compareKey 기준)
const aliasIndex = new Map<string, string>();
TECH_MASTER.forEach((t) => {
  const keys = [t.code, t.label, t.displayKo, ...(t.aliases || [])];
  keys.forEach((k) => {
    const ck = compareKey(k);
    if (ck && !aliasIndex.has(ck)) aliasIndex.set(ck, t.code);
  });
});

/**
 * 기술 분류 DB 목록 반영 — PARTNER_TECH_CODES 가 바뀐 뒤(applyPartnerTechCodes) 부른다.
 *   새 코드는 TECH_MASTER 에 더하고, 이름이 바뀐 코드는 표기를 고치고, 사람이 더한 별칭은 회의 배지 · 정규화 사전에 더한다.
 */
export function syncTechMaster(extraAliases: Record<string, string[]> = {}) {
  for (const c of PARTNER_TECH_CODES) {
    let e = masterByCode.get(c.code);
    if (!e) {
      e = { code: c.code, label: c.name, displayKo: c.name, category: GROUP_LABEL.get(c.group) || c.group, group: ENGINE[c.code]?.group || "equipment", aliases: [] };
      TECH_MASTER.push(e);
      masterByCode.set(c.code, e);
    } else {
      e.label = c.name;
      e.displayKo = c.name;
      e.category = GROUP_LABEL.get(c.group) || c.group;
    }
    const base = ENGINE[c.code]?.aliases || [];
    const extra = extraAliases[c.code] || [];
    e.aliases = Array.from(new Set([...base, ...extra]));
    for (const k of [e.code, e.label, e.displayKo, ...e.aliases]) {
      const ck = compareKey(k);
      if (ck && !aliasIndex.has(ck)) aliasIndex.set(ck, e.code);
    }
  }
}

export type NormalizedTech = {
  /** 25코드 — 미등록이면 UNCLASSIFIED_CODE */
  code: string;
  /** 원문 그대로 (미분류 보존·감사용) */
  raw: string;
  entry?: TechMasterEntry;
  unclassified: boolean;
};

/**
 * 자유텍스트 tech 표기 1건 정규화. 오타(Pakaging)·대소문자·공백·특수문자 변형을 흡수하고
 * 미등록 값은 버리지 않고 unclassified=true 로 보존한다(신규 파트너의 새 tech 대응).
 */
export function normalizeTechName(raw: string): NormalizedTech {
  const trimmed = String(raw || "").trim();
  const code = aliasIndex.get(compareKey(trimmed));
  if (code) return { code, raw: trimmed, entry: masterByCode.get(code), unclassified: false };
  return { code: UNCLASSIFIED_CODE, raw: trimmed, unclassified: true };
}

/** 문자열 배열(파트너 technologies) 정규화 — 빈값 제거, 코드 중복 제거(미분류는 원문별 유지) */
export function normalizeTechList(rawList: Array<string | null | undefined>): NormalizedTech[] {
  const out: NormalizedTech[] = [];
  const seenCodes = new Set<string>();
  const seenUnclassified = new Set<string>();
  (rawList || []).forEach((r) => {
    const trimmed = String(r || "").trim();
    if (!trimmed) return;
    const n = normalizeTechName(trimmed);
    if (n.unclassified) {
      const k = compareKey(trimmed);
      if (seenUnclassified.has(k)) return;
      seenUnclassified.add(k);
    } else {
      if (seenCodes.has(n.code)) return;
      seenCodes.add(n.code);
    }
    out.push(n);
  });
  return out;
}

// ── component_code(Vault 패키지 파싱 산물) → 25코드 (사용자 확정 표 + 2026-11-02 코드 통일) ──
//  ★ 슬래시·언더스코어를 알고리즘으로 쪼개지 말 것 — 명시 테이블로만 판정한다.
//  코드 통일로 DPS_DAS · PALLETIZER_DEPALLETIZER 는 각각 코드 하나(PIC-DPSDAS · PIC-PAL)가 됐다 —
//  예전 「둘 다 보유해야 매칭(AND)」은 같은 코드라 자연히 한 번 판정이다.
const COMPONENT_CODE_MAP: Record<string, string[]> = {
  WMS: ["SW-WMS"],
  WCS: ["SW-WCS"],
  CONVEYOR: ["MOV-CONV"],
  SCANNER: ["SEN-BAR"],
  RFID: ["SEN-BAR"],
  VISION: ["SEN-VIS"],
  DPS: ["PIC-DPSDAS"],
  PDA: ["SEN-BAR"],
  SORTER_3D: ["SOR-SORT"],
  SHUTTLE: ["STO-SHUT"],
  "E-SORT": ["SOR-SORT"],
  PALLETIZER_DEPALLETIZER: ["PIC-PAL"],
  AMR: ["MOV-AMR"],
  "랩핑기": ["PAC-PACK"],
  AUTO_STORE: ["STO-AUTO"],
  DPS_DAS: ["PIC-DPSDAS"],
  SCADA: ["SW-WCS"],
  "제함&봉함기": ["PAC-PACK"],
  AMR_FLEET: ["MOV-AMR"], // Fleet 별도 코드 없음
  INDICATOR: ["PIC-DPSDAS"], // 부품 → DAS 흡수
  STACKER_CRANE: ["STO-ASRS"],
  DAS: ["PIC-DPSDAS"],
  WEARABLE: ["SEN-BAR"],
  VOICE: ["PIC-VOICE"],
};

const componentCodeIndex = new Map<string, string[]>();
Object.entries(COMPONENT_CODE_MAP).forEach(([k, v]) => componentCodeIndex.set(compareKey(k), v));

/**
 * component_code(패키지 파싱 산물) → 매칭에 필요한 25코드 목록.
 * 매핑에 없으면 별칭 사전으로 단일 해소, 그래도 미등록이면 원문 exact 매칭용 sentinel("u:키") 1개.
 */
export function resolveComponentCodes(componentCode: string): string[] {
  const ck = compareKey(componentCode);
  if (!ck) return [];
  const mapped = componentCodeIndex.get(ck);
  if (mapped) return mapped.slice();
  const n = normalizeTechName(componentCode);
  return n.unclassified ? ["u:" + ck] : [n.code];
}

/**
 * 파트너 보유 값(tech_codes · 옛 weight_key) → 25코드 목록.
 * 옛 복합 표기 「AGV/AMR」은 두 코드. 미등록이면 원문 exact 매칭용 sentinel("u:키") 1개.
 */
export function expandTechName(raw: string): string[] {
  const ck = compareKey(raw);
  if (!ck) return [];
  if (ck === "agvamr") return ["MOV-AMR", "MOV-AGV"];
  const n = normalizeTechName(raw);
  return n.unclassified ? ["u:" + ck] : [n.code];
}

export const TECH_GROUP_LABEL: Record<TechGroup, string> = {
  equipment: "설비",
  software: "소프트웨어",
  device: "디바이스",
  service: "서비스",
};

/** TECH_GROUP_LABEL 의 영문 — 화면 언어가 en 일 때. */
export const TECH_GROUP_LABEL_EN: Record<TechGroup, string> = {
  equipment: "Equipment",
  software: "Software",
  device: "Device",
  service: "Service",
};
export const UNCLASSIFIED_LABEL_EN = "Unclassified";

export function techGroupLabel(group: TechGroup, language: "ko" | "en" = "ko"): string {
  return (language === "en" ? TECH_GROUP_LABEL_EN : TECH_GROUP_LABEL)[group] || group;
}

/** 화면 영문 표기 — 화면 언어가 en 일 때만(표시 전용, 별칭 사전에는 안 넣는다). */
export const TECH_DISPLAY_EN: Record<string, string> = {
  ...Object.fromEntries(Object.entries(ENGINE).map(([code, e]) => [code, e.en])),
  [UNCLASSIFIED_CODE]: UNCLASSIFIED_LABEL_EN,
};

/** 추정 스펙 표기 — en 이면 spec.labelEn(없으면 label). 코드만 알 때는 마스터에서 찾는다. */
export function techSpecLabel(
  specOrCode: Pick<TechSpec, "label" | "labelEn"> | string | null | undefined,
  language: "ko" | "en" = "ko",
  fallback = "",
): string {
  const spec = typeof specOrCode === "string" ? getTechEntry(specOrCode)?.spec : specOrCode;
  if (!spec) return fallback;
  return (language === "en" && spec.labelEn) || spec.label || fallback;
}

/** 화면 표기 — ko 면 displayKo, en 이면 TECH_DISPLAY_EN(없으면 displayKo). 코드가 아니면 별칭으로 찾는다. */
export function techDisplayLabel(
  entryOrCode: Pick<TechMasterEntry, "code" | "displayKo"> | string,
  language: "ko" | "en" = "ko",
): string {
  const entry = typeof entryOrCode === "string" ? getTechEntry(entryOrCode) || normalizeTechName(entryOrCode).entry : entryOrCode;
  const code = entry?.code || (typeof entryOrCode === "string" ? entryOrCode : entryOrCode.code);
  if (language === "en" && TECH_DISPLAY_EN[code]) return TECH_DISPLAY_EN[code];
  if (entry) return entry.displayKo;
  return code === UNCLASSIFIED_CODE ? UNCLASSIFIED_LABEL : code;
}
